import { getSupabaseClient } from '@/integrations/supabase/client';
import { SIMPLE_USER_ROLES, type SimpleUser, type SimpleUserRole } from '@/types/simpleUser';

export interface CreateSimpleUserParams {
  username: string;
  pin: string;
  organizationId: string;
}

function normalizeRole(value: unknown): SimpleUserRole {
  return value === 'manager' ? 'manager' : 'cashier';
}

function mapRow(row: Record<string, unknown>): SimpleUser {
  return {
    id: String(row.id),
    organization_id: String(row.organization_id),
    username: String(row.username),
    is_active: Boolean(row.is_active),
    role: normalizeRole(row.role),
    created_at: String(row.created_at),
    updated_at: String(row.updated_at),
  };
}

export const simpleUsersService = {
  async createSimpleUser({ username, pin, organizationId }: CreateSimpleUserParams): Promise<string> {
    const supabase = getSupabaseClient();

    if (!/^\d{4}$/.test(pin)) {
      throw new Error('PIN must be exactly 4 digits');
    }

    const { data, error } = await supabase.from('simple_users').
    insert({ username: username.trim(), pin: pin, organization_id: organizationId }).select().single();

    if (error) throw error;
    return data as string;
  },

  async resetPIN(simpleUserId: string, newPin: string): Promise<void> {
    const supabase = getSupabaseClient();

    if (!/^\d{4}$/.test(newPin)) {
      throw new Error('PIN must be exactly 4 digits');
    }

    const { error } = await supabase.from('simple_users').update({ pin: newPin }).eq('id', simpleUserId);
    if (error) throw error;
  },

  async updateSimpleUserUsername(
    simpleUserId: string,
    username: string,
    organizationId: string,
  ): Promise<void> {
    const supabase = getSupabaseClient();
    const trimmed = username.trim();
    if (trimmed.length < 2 || trimmed.length > 50) {
      const error = new Error('Username must be between 2 and 50 characters');
      error.name = 'INVALID_USERNAME';
      throw error;
    }

    const { data, error } = await supabase
      .from('simple_users')
      .update({ username: trimmed, updated_at: new Date().toISOString() })
      .eq('id', simpleUserId)
      .eq('organization_id', organizationId)
      .select('id');
    if (error) throw error;
    if (!data || data.length === 0) {
      throw new Error('Username update was not applied. You may not have permission to change this simple user.');
    }
  },

  async listSimpleUsers(organizationId: string, includeInactive = false): Promise<SimpleUser[]> {
    const supabase = getSupabaseClient();
    const { data, error } = includeInactive ? await supabase.
    from('simple_users').select('id, username, is_active, role, created_at, updated_at').eq('organization_id', organizationId) : await supabase.
    from('simple_users').select('id, username, is_active, role').eq('organization_id', organizationId).eq('is_active', true);
    if (error) throw error;
    return (data ?? []).map((row) => mapRow(row as Record<string, unknown>));
  },

  async updateSimpleUserRole(
    simpleUserId: string,
    role: SimpleUserRole,
    organizationId: string,
  ): Promise<void> {
    const supabase = getSupabaseClient();
    if (!SIMPLE_USER_ROLES.includes(role)) {
      throw new Error('Invalid simple user role');
    }
    const { data, error } = await supabase
      .from('simple_users')
      .update({ role, updated_at: new Date().toISOString() })
      .eq('id', simpleUserId)
      .eq('organization_id', organizationId)
      .select('id');
    if (error) throw error;
    if (!data || data.length === 0) {
      throw new Error('Role update was not applied. You may not have permission to change this simple user.');
    }
  },

  async deactivateSimpleUser(simpleUserId: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('simple_users')
      .update({ is_active: false, updated_at: new Date().toISOString() })
      .eq('id', simpleUserId);
    if (error) throw error;
  },

  async activateSimpleUser(simpleUserId: string): Promise<void> {
    const supabase = getSupabaseClient();
    const { error } = await supabase
      .from('simple_users')
      .update({ is_active: true, updated_at: new Date().toISOString() })
      .eq('id', simpleUserId);
    if (error) throw error;
  },

  async getActiveSimpleUser(simpleUserId: string): Promise<SimpleUser | null> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('simple_users')
      .select('id, organization_id, username, is_active, role, created_at, updated_at')
      .eq('id', simpleUserId)
      .eq('is_active', true)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    return mapRow(data as Record<string, unknown>);
  },

  async loginSimpleUser(simpleUserId: string, pin: string): Promise<SimpleUser | null> {
    const supabase = getSupabaseClient();
    const { data, error } = await supabase
      .from('simple_users')
      .select('id, organization_id, username, is_active, role, created_at, updated_at')
      .eq('id', simpleUserId)
      .eq('pin', pin)
      .eq('is_active', true)
      .maybeSingle();

    if (error) throw error;
    if (!data) return null;
    return mapRow(data as Record<string, unknown>);
  },
};
